import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ResponsiveFormLayout from '../../../shared/components/layout/ResponsiveFormLayout';
import ProfileProgressBar from '../components/ProfileProgressBar';
import { useLanguage } from '../../../shared/contexts/LanguageContext';
import { useStepRedirect, skipNextStepRedirect } from '../../../hooks/useStepRedirect';
import { useProgressWithAutoRefresh } from '../../../shared/contexts/ProgressContext';
import { RegistrationApiService } from '../../registration/api/RegistrationApiService';

interface PaymentScreenProps {
  onNext: () => void;
  onBack: () => void;
  onError: (error: string) => void;
}

const PaymentScreen: React.FC<PaymentScreenProps> = ({ onNext, onBack, onError }) => {
  // Protect this page - redirect if doctor hasn't reached payment step yet
  useStepRedirect();

  const navigate = useNavigate();
  const location = useLocation();
  const { isRTL, t } = useLanguage();
  const { progress, refreshProgress } = useProgressWithAutoRefresh();
  // The context recreates refreshProgress whenever progress changes; read it through a ref so the payment
  // callbacks below (and the polling effect that depends on them) keep their identity.
  const refreshProgressRef = useRef(refreshProgress);
  refreshProgressRef.current = refreshProgress;
  const [loading, setLoading] = useState(false);
  const [pollingActive, setPollingActive] = useState(false);
  const [paymentId, setPaymentId] = useState<string | null>(null);
  // Payment reference of the payment being watched: lets a check ask Qi Card through the server.
  const [paymentReference, setPaymentReference] = useState<string | null>(null);
  // Result of the last Qi Card attempt, kept on screen (a toast is gone before a doctor coming back
  // from Qi has read it). A cancellation is neutral, not an error.
  const [lastResult, setLastResult] = useState<'failed' | 'cancelled' | null>(null);
  const [registrationApi] = useState(() => new RegistrationApiService());
  const hasCheckedReturn = useRef(false);

  // Check location state first for request type (passed from ClosureDocumentsScreen)
  const locationState = location.state as { requestType?: string; requestId?: string } | null;

  // Detect if this is a closure request - check location state first, then progress data
  const closureRequestFromProgress = progress?.active_requests?.find(
    (r: any) => r.request_type === 'closure'
  );
  const isClosurePayment = locationState?.requestType === 'closure' || !!closureRequestFromProgress;
  const closureRequestId = locationState?.requestId || closureRequestFromProgress?.request_id;

  // Get registration request from progress
  const registrationRequestFromProgress = progress?.active_requests?.find(
    (r: any) => r.request_type === 'registration'
  );
  const registrationRequestId = registrationRequestFromProgress?.request_id;

  // Payment amounts differ by request type and are set per-deployment on the
  // backend (PAYMENT_*_FEE). Never hardcode them here: the closure fee was raised
  // on the API while this screen still advertised the old price. The constants
  // below are only a first-paint fallback for the split second before /fees
  // answers (and if it fails) — they mirror the backend defaults.
  const FALLBACK_FEES = { registration: 125000, closure: 100000 };
  const [fees, setFees] = useState<{ registration: number; closure: number }>(FALLBACK_FEES);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await registrationApi.getPaymentFees();
      if (cancelled || !result.isSuccess || !result.data) return;
      setFees({ registration: result.data.registration, closure: result.data.closure });
    })();
    return () => {
      cancelled = true;
    };
  }, [registrationApi]);

  const paymentAmount = isClosurePayment ? fees.closure : fees.registration;

  // Doctor payment routes answer 404 (never 403) for a payment or request that is not this doctor's.
  const paymentNotFoundText = isRTL
    ? 'لم نعثر على عملية الدفع هذه في حسابك. تحقّق من حالة طلبك في الصفحة الرئيسية قبل الدفع مرة أخرى.'
    : "We could not find this payment on your account. Check your request's status on the home page before paying again.";
  const requestNotFoundText = isRTL
    ? 'لم نعثر على طلب بانتظار الدفع في حسابك. ارجع إلى الصفحة الرئيسية لمتابعة طلباتك.'
    : 'We could not find a request awaiting payment on your account. Go back to the home page to follow your requests.';

  const pollPaymentStatus = useCallback(async (paymentIdToCheck: string, reference?: string | null) => {
    console.log('Checking payment status for:', paymentIdToCheck);

    try {
      // With a reference, have the server re-check with Qi first, so a lost or late webhook does not
      // keep the doctor waiting; a failed re-check falls back to the stored status below.
      if (reference) await registrationApi.verifyPayment(reference);
      const result = await registrationApi.checkPaymentStatus(paymentIdToCheck);
      console.log('Payment status result:', result);

      if (result.isSuccess) {
        const paymentStatus = result.data?.status?.toLowerCase();
        console.log('Normalized payment status:', paymentStatus);

        if (paymentStatus === 'success' || paymentStatus === 'completed' || paymentStatus === 'paid') {
          console.log('Payment completed successfully!');
          localStorage.removeItem('paymentId');
          setPaymentId(null);
          setPollingActive(false);
          setLoading(false);
          // Load the now-submitted request into progress BEFORE leaving: a /progress fetched before the
          // payment completed (still terms_agreed) would otherwise reach /success after us and send the
          // doctor back to this page.
          await refreshProgressRef.current(true);
          skipNextStepRedirect();
          navigate('/success');
          return true;
        }

        if (paymentStatus === 'failed' || paymentStatus === 'cancelled' || paymentStatus === 'rejected') {
          console.log('Payment failed');
          localStorage.removeItem('paymentId');
          setPaymentId(null);
          setPollingActive(false);
          setLoading(false);
          setLastResult(paymentStatus === 'cancelled' ? 'cancelled' : 'failed');
          return true;
        }

        console.log('Payment still processing, status:', result.data.status);
        return false;
      } else {
        console.log('Payment status check failed:', result.error);
        // 404: the payment is not (or no longer) on this account — e.g. an id left over from another
        // sign-in. Stop polling and let the doctor start again instead of checking forever.
        if (result.errorDetails?.status === 404) {
          localStorage.removeItem('paymentId');
          setPaymentId(null);
          setPollingActive(false);
          setLoading(false);
          onError(paymentNotFoundText);
          return true;
        }
        return false;
      }
    } catch (error) {
      console.error('Network error checking payment status:', error);
      return false;
    }
  }, [registrationApi, t, onError, paymentNotFoundText]);

  // Helper: call initiate to get or create a pending payment
  const callInitiate = useCallback(async () => {
    const returnUrl = `${window.location.origin}/payment?payment_return=true`;
    const cancelUrl = `${window.location.origin}/payment?payment_return=true`;

    const result = isClosurePayment && closureRequestId
      ? await registrationApi.initiatePayment(returnUrl, cancelUrl, closureRequestId, 'closure')
      : await registrationApi.initiatePayment(returnUrl, cancelUrl, registrationRequestId, 'registration');

    return result;
  }, [registrationApi, isClosurePayment, closureRequestId, registrationRequestId]);

  // On mount: if returning from Qi Card, call initiate to recover the pending payment and start polling
  useEffect(() => {
    if (hasCheckedReturn.current) return;

    const params = new URLSearchParams(window.location.search);
    const isReturn = params.get('payment_return') === 'true';

    if (!isReturn) return;
    // Coming back from Qi Card is a full page load: wait until /progress names the request, otherwise
    // initiate has no request id and the pending payment is never recovered (nor polled).
    const returnRequestId = isClosurePayment ? closureRequestId : registrationRequestId;
    if (!returnRequestId) return;
    hasCheckedReturn.current = true;

    const recoverPayment = async () => {
      setLoading(true);
      try {
        // Read the payment this device just sent to Qi Card first. A final result (failed, cancelled,
        // paid) is shown as is: calling initiate for a failed payment makes the server open a NEW
        // pending payment, and this page then waited on that one forever instead of saying the payment
        // failed. Only without such a payment (another device, cleared storage) or while it is still
        // pending do we recover through initiate, which returns the request's live pending payment.
        const storedId = localStorage.getItem('paymentId');
        if (storedId) {
          const own = await registrationApi.checkPaymentStatus(storedId);
          const ownStatus = own.isSuccess ? String(own.data?.status || '').toLowerCase() : '';
          const ownType = own.isSuccess ? String(own.data?.request_type || '') : '';
          if (ownStatus && ownStatus !== 'pending' && ownType === (isClosurePayment ? 'closure' : 'registration')) {
            await pollPaymentStatus(storedId, own.data?.payment_reference);
            return;
          }
        }

        const result = await callInitiate();

        if (result.isSuccess && result.data?.payment_id) {
          localStorage.setItem('paymentId', String(result.data.payment_id));
          setPaymentReference(result.data.payment_reference || null);
          setPaymentId(result.data.payment_id);
          setPollingActive(true);
        }
      } catch (error) {
        console.error('Failed to recover payment on return:', error);
      } finally {
        setLoading(false);
      }
    };

    recoverPayment();
  }, [callInitiate, isClosurePayment, closureRequestId, registrationRequestId, registrationApi, pollPaymentStatus]);

  // Polling interval effect
  useEffect(() => {
    if (!pollingActive || !paymentId) return;

    console.log('Starting background polling every 20 seconds for payment:', paymentId);

    pollPaymentStatus(paymentId, paymentReference);

    const intervalId = setInterval(async () => {
      if (paymentId) {
        const shouldStop = await pollPaymentStatus(paymentId, paymentReference);
        if (shouldStop) {
          clearInterval(intervalId);
        }
      }
    }, 20000);

    return () => {
      console.log('Stopping background polling');
      clearInterval(intervalId);
    };
  }, [pollingActive, paymentId, paymentReference, pollPaymentStatus]);

  const initiatePayment = async () => {
    setLoading(true);

    try {
      console.log('💳 Initiating payment...');
      console.log('💳 Is closure payment:', isClosurePayment);

      setLastResult(null);
      const result = await callInitiate();

      console.log('💳 Payment initiation result:', result);

      if (result.isSuccess) {
        console.log('💳 Payment ID:', result.data.payment_id);

        const paymentUrl = result.data.payment_url || result.data.form_url || result.data.redirect_url || result.data.url;

        if (!paymentUrl) {
          console.error('No payment URL found in response:', result.data);
          throw new Error(t('payment_url_not_found'));
        }

        // Remember which payment went to Qi Card, so the return page can show its own result.
        if (result.data.payment_id) localStorage.setItem('paymentId', String(result.data.payment_id));
        console.log('💳 Redirecting to:', paymentUrl);
        window.location.href = paymentUrl;
      } else {
        console.error('Payment initiation failed:', result.error);
        throw new Error(result.errorDetails?.status === 404 ? requestNotFoundText : result.error || t('failed_initiate_payment'));
      }
    } catch (error) {
      console.error('Payment initiation error:', error);
      const errorMessage = error instanceof Error ? error.message : t('failed_initiate_payment_try_again');
      onError(errorMessage);
      setLoading(false);
    }
  };

  // Staging-only: skip payment. Visible only when the staging build enables it; the
  // backend hard-gates it (returns 404 in production), so it can never work in prod.
  const bypassEnabled = process.env.REACT_APP_ALLOW_PAYMENT_BYPASS === 'true';
  const handleBypassPayment = async () => {
    const reqId = isClosurePayment ? closureRequestId : registrationRequestId;
    const reqType: 'closure' | 'registration' = isClosurePayment ? 'closure' : 'registration';
    if (!reqId) {
      onError(t('failed_initiate_payment'));
      return;
    }
    setLoading(true);
    try {
      const result = await registrationApi.bypassPayment(reqId, reqType);
      if (result.isSuccess) {
        await refreshProgressRef.current(true);
        skipNextStepRedirect();
        navigate('/success');
      } else {
        onError(result.error || t('failed_initiate_payment'));
      }
    } catch (error) {
      onError(error instanceof Error ? error.message : t('failed_initiate_payment'));
    } finally {
      setLoading(false);
    }
  };

  const handleManualCheck = async () => {
    if (!paymentId) return;

    setLoading(true);
    await pollPaymentStatus(paymentId, paymentReference);
    setLoading(false);
  };

  const handleCancelPayment = async () => {
    if (!paymentId) return;

    setLoading(true);

    try {
      console.log('Canceling payment:', paymentId);
      const result = await registrationApi.cancelPayment(paymentId);

      if (result.isSuccess) {
        console.log('Payment cancelled successfully');
        localStorage.removeItem('paymentId');
        setPaymentId(null);
        setPollingActive(false);
        setLoading(false);
        onError(t('payment_cancelled_successfully'));
      } else if (result.errorDetails?.status === 404) {
        // Nothing to cancel on this account any more.
        localStorage.removeItem('paymentId');
        setPaymentId(null);
        setPollingActive(false);
        setLoading(false);
        onError(paymentNotFoundText);
      } else {
        throw new Error(result.error || t('failed_cancel_payment'));
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('failed_cancel_payment_try_again');
      onError(errorMessage);
      setLoading(false);
    }
  };

  const actionButtons = (
    <div className="space-y-3">
      {!paymentId ? (
        <>
          <button
            onClick={initiatePayment}
            disabled={loading}
            className={`w-full min-h-[56px] px-6 py-4 rounded-2xl text-white text-base font-semibold transition-all duration-200 flex items-center justify-center gap-3 ${
              loading
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:opacity-90 cursor-pointer shadow-md hover:shadow-lg'
            }`}
            style={{ backgroundColor: '#2E5D7D' }}
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                <span>{t('processing')}</span>
              </div>
            ) : (
              t('pay_with_qi_card')
            )}
          </button>

          {bypassEnabled && (
            <button
              onClick={handleBypassPayment}
              disabled={loading}
              className={`w-full min-h-[48px] px-6 py-3 rounded-2xl border-2 border-dashed border-amber-500 text-amber-700 text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${
                loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-amber-50 cursor-pointer'
              }`}
            >
              {t('skip_payment_staging')}
            </button>
          )}

        </>
      ) : (
        <div className="space-y-3">
          <button
            onClick={handleManualCheck}
            disabled={loading}
            className={`w-full min-h-[56px] px-6 py-4 rounded-2xl text-white text-base font-semibold transition-all duration-200 flex items-center justify-center gap-3 ${
              loading
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:opacity-90 cursor-pointer shadow-md hover:shadow-lg'
            }`}
            style={{ backgroundColor: '#2E5D7D' }}
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                <span>{t('verifying_payment')}</span>
              </div>
            ) : pollingActive ? (
              <div className="flex items-center gap-2">
                <div className="animate-pulse w-2 h-2 bg-white rounded-full"></div>
                <span>{t('check_payment_status')}</span>
                <span className="text-xs opacity-75">{t('auto_checking')}</span>
              </div>
            ) : (
              t('check_payment_status')
            )}
          </button>

          <button
            onClick={handleCancelPayment}
            disabled={loading}
            className={`w-full min-h-[48px] px-6 py-3 rounded-2xl border-2 border-red-500 text-red-600 text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${
              loading
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:bg-red-50 hover:border-red-600 cursor-pointer'
            }`}
          >
            {t('cancel_payment')}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <ResponsiveFormLayout
      title={isClosurePayment ? (t('clinic_closure') || 'Clinic Closure') : t('open_clinic')}
      subtitle={t('fees')}
      actions={actionButtons}
      progressValue={95}
      className={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Profile Progress Bar - only show for registration */}
      {!isClosurePayment && <ProfileProgressBar />}

      <div className="space-y-6">
        {/* Payment Amount */}
        <div className="text-center">
          <p className={`text-lg text-gray-800 ${isRTL ? 'text-right' : 'text-left'}`}>
            {isClosurePayment
              ? (t('closure_fee_iqd') || t('registration_fee_iqd')).replace('{amount}', paymentAmount.toLocaleString())
              : t('registration_fee_iqd').replace('{amount}', paymentAmount.toLocaleString())
            }
          </p>
        </div>

        {/* QI Card Payment Method */}
        <div className="space-y-4">
          <div className={`${isRTL ? 'text-right' : 'text-left'}`}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t('payment_method')}
            </h3>
          </div>
        </div>

        {/* Result of the last Qi Card attempt */}
        {lastResult && !pollingActive && (
          <div
            role="status"
            className={`rounded-xl p-4 border ${lastResult === 'failed' ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-300'}`}
          >
            <p className={`text-sm font-semibold ${lastResult === 'failed' ? 'text-red-800' : 'text-gray-900'} ${isRTL ? 'text-right' : 'text-left'}`}>
              {lastResult === 'failed' ? t('payment_failed_try_again') : t('payment_was_cancelled_title')}
            </p>
            {lastResult === 'cancelled' && (
              <p className={`text-sm text-gray-700 mt-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                {t('payment_was_cancelled_body')}
              </p>
            )}
          </div>
        )}

        {/* Payment Process Information */}
        {pollingActive && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="animate-pulse w-3 h-3 bg-blue-500 rounded-full"></div>
              <div className="flex-1">
                <p className={`text-blue-800 text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('auto_monitoring_active')}
                </p>
                <p className={`text-blue-700 text-xs mt-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('manual_check_hint')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Payment Security Notice */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-gray-400 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
              <span className="text-white text-xs">🔒</span>
            </div>
            <div className="flex-1">
              <h4 className={`font-semibold text-gray-900 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                {t('secure_payment')}
              </h4>
              <p className={`text-sm text-gray-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                {t('secure_payment_description')}
              </p>
            </div>
          </div>
        </div>

        {/* Payment Instructions */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
              <span className="text-white text-xs">ℹ</span>
            </div>
            <div className="flex-1">
              <h4 className={`font-semibold text-amber-900 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                {t('payment_instructions')}
              </h4>
              <ul className={`text-sm text-amber-800 space-y-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600 mt-1">•</span>
                  <span>{t('redirect_to_qi')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600 mt-1">•</span>
                  <span>{t('required_amount_iqd').replace('{amount}', paymentAmount.toLocaleString())}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600 mt-1">•</span>
                  <span>{t('auto_verify_payment')}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </ResponsiveFormLayout>
  );
};

export default PaymentScreen;
